import { DealAPI } from '@business/api/deal';
import { CompactDealResponse, DealId, DealRequest, DealResponse, DealStatus } from '@business/entities/deal';
import { OrgId } from '@business/entities/organization';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation, showConfirmation, showYesNo } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { ProposalTemplateId, ProposalId } from '@business/entities/proposal';

export class DealBloc extends BaseBloc {
  private readonly dealRepo = new Repository<CompactDealResponse | DealResponse>({
    getItemId: deal => deal.id,
  });

  readonly deals$ = this.dealRepo.items$;
  readonly selectDeal = this.dealRepo.selectItem;

  onReset() {
    this.dealRepo.reset();
  }

  fetchDeals = () => {
    return DealAPI.fetchDeals().pipe(this.dealRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_pros_error') }));
  };

  fetchDeal = (id: DealId) => {
    return DealAPI.fetchDeal(id).pipe(
      this.dealRepo.ops.upsertOne(item => ({ item })),

      handleMessage({ error: requestMessage('fetch_pros_error') }),
    );
  };

  createDeal = (orgId: OrgId, deal: DealRequest, proposalId?: ProposalId) => {
    return DealAPI.createDeal(orgId, deal, proposalId).pipe(
      this.dealRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_deal'),
      }),
    );
  };

  updateDeal = (id: DealId, deal: DealRequest) => {
    return DealAPI.updateDeal(id, deal).pipe(
      this.dealRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_deal'),
      }),
    );
  };

  deleteDeal = (id: DealId) => {
    return showDeleteConfirmation('Delete Deal', 'Do you really want to remove this deal?').pipe(
      switchMap(() => DealAPI.deleteDeal(id)),
      this.dealRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_deal'),
      }),
    );
  };

  updateDealStatus = (id: DealId, status: DealStatus, lostReason?: string, lostExplain?: string, archiveProposals = false) => {
    return DealAPI.updateDealStatus(id, status, lostReason, lostExplain, archiveProposals).pipe(
      this.dealRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('deal_status'),
      }),
    );
  };

  updateDealStatusMultiple = (ids: DealId[], status: DealStatus, lostReason?: string, lostExplain?: string) => {
    return DealAPI.updateDealStatusMultiple(ids, status, lostReason, lostExplain).pipe(
      handleMessage({
        type: requestMessage('deal_status'),
      }),
    );
  };

  updateDealStage = (id: DealId, stageId: string) => {
    return DealAPI.updateDealStageFull(id, stageId).pipe(
      this.dealRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('deal_status'),
      }),
    );
  };

  updateDealAction = (id: DealId, actionId: string, actionName: string) => {
    return DealAPI.updateDealAction(id, actionId, actionName).pipe(
      this.dealRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_deal'),
      }),
    );
  };

  updateClosedDate = (id: DealId, dueDate: number) => {
    return DealAPI.updateClosedDate(id, dueDate).pipe(
      this.dealRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_deal'),
      }),
    );
  };

  setVendastaAccounts = (orgId: OrgId, id: string, name: string, dealId: DealId) => {
    return DealAPI.setVendastaAccounts(orgId, id, name).pipe(
      this.dealRepo.ops.updateOne(organization => ({
        id: dealId,
        changes: () => ({ organization }),
      })),
    );
  };

  addProposal = (dealId: DealId, ProposalTemplateId: ProposalTemplateId) => {
    return showYesNo('Update Deal Value', 'Do you want to update deal value?').pipe(
      switchMap(result => {
        return DealAPI.addProposal(dealId, ProposalTemplateId, result).pipe(this.dealRepo.ops.upsertOne(item => ({ item })));
      }),
    );
  };

  deleteProposal = (dealId: DealId, ProposalTemplateId: ProposalTemplateId) => {
    return showDeleteConfirmation('Delete Deal', 'Do you really want to remove this proposal?').pipe(
      switchMap(() => DealAPI.deleteProposal(dealId, ProposalTemplateId)),
      this.dealRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('unlink_proposal'),
      }),
    );
  };
}
